/**
 * Auth context object, types and hooks.
 *
 * Split out of AuthContext.tsx so that the .tsx module exports ONLY the
 * AuthProvider component (keeps React Fast Refresh + the
 * `react-refresh/only-export-components` lint rule clean).
 */
import { createContext, useContext } from "react";
import type { LoginResponse, User } from "../api/client";
import { usePreviewPersonaOptional } from "./previewPersona";
import { PARALEGAL_DESK_PRESETS, worksFinanceDesk } from "../components/layout/crmNav";
import type { EffectiveAuth } from "./previewPersona";

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  /** Re-fetch the current user (e.g. after a forced password set). */
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * The EFFECTIVE auth identity the UI should gate against (#1383).
 *
 * Normally this is just the real signed-in user (admins all-true). BUT when an
 * admin is PREVIEWING-AS a persona (the workspace switcher's view-as-persona
 * mode), it returns the SIMULATED persona's role / preset / flags instead, so
 * every visibility predicate -- ``usePermissions`` below, ``CrmSidebar``,
 * ``ProtectedRoute`` -- computes against the same definition a real persona
 * login would render. That single shared source is what keeps "preview" and
 * "real" from ever diverging.
 *
 * SECURITY: this is FRONTEND masking only. The real token is unchanged and
 * still holds full admin privileges -- no server guard is reduced.
 */
export function useEffectiveAuth(): EffectiveAuth {
  const { user } = useAuth();
  const { previewing, active } = usePreviewPersonaOptional();
  if (previewing && active) {
    return {
      role: active.role,
      preset: active.permission_preset,
      flags: active.permission_flags,
      previewing: true,
      farmWideCanvass: false,
      hasOwnBook: false,
    };
  }
  return {
    role: user?.role,
    preset: user?.permission_preset ?? "none",
    flags: user?.permission_flags,
    previewing: false,
    farmWideCanvass: Boolean(user?.farm_wide_canvass),
    hasOwnBook: Boolean(user?.has_own_book),
  };
}

/**
 * Convenience hook for deal-finance permission flags (D4-AUTH-001).
 *
 * Reads the EFFECTIVE ``permission_flags`` -- the real user's normally, or the
 * SIMULATED persona's while an admin previews-as (#1383, via
 * ``useEffectiveAuth``). Used to gate the /legal and /finance surfaces and to
 * mask commission UI -- the backend is the source of truth, this only keeps the
 * UI consistent with it (and faithful to the previewed persona).
 */
export function usePermissions() {
  const { flags, preset, role, farmWideCanvass, hasOwnBook, previewing } = useEffectiveAuth();
  // A super-user is a per-user fact, never part of a preview-as persona, so it
  // only counts for the real signed-in identity.
  const { user } = useAuth();
  const isSuperuser = !previewing && Boolean(user?.is_superuser);
  const has = (flag: keyof NonNullable<typeof flags>): boolean =>
    Boolean(flags?.[flag]);
  const effectivePreset = preset ?? "none";
  return {
    preset: effectivePreset,
    role: role ?? null,
    canCaptureDeal: has("capture_deal"),
    canViewCommission: has("view_commission"),
    canEnterOwnCommission: has("enter_own_commission"),
    canAuthorizeCommission: has("authorize_commission"),
    canProcessPayment: has("process_payment"),
    canSignOffDeals: has("sign_off_deals"),
    canInvestFunds: has("invest_funds"),
    // WP8a (#1299): scoped user management (Manager / Office Admin / admin).
    canManageUsers: has("manage_users"),
    // #1411: management oversight -- read-only company-wide dashboards.
    canViewManagementDashboards: has("view_management_dashboards"),
    // D5-RBP-003 (#1412): Office Admin listing capability (narrow; NOT capture_deal).
    canListProperties: has("list_properties"),
    // #3032 / #4220 (D2-OTP-067): may hand-correct a deal's File No. Mirrors the
    // server's CanCorrectFileNumber exactly: the admin role, the Office Admin
    // `list_properties` capability, or the paralegal desk (paralegal and the
    // combined HR/Paralegal seat). Finance / manager / accounts stay out.
    canCorrectFileNumber:
      role === "admin" ||
      has("list_properties") ||
      (PARALEGAL_DESK_PRESETS as readonly string[]).includes(effectivePreset),
    // #4364 / #4447: may unlink / move a listing off a farm record. Mirrors the
    // server's CanCorrectFarmListingLink exactly: the admin role or a
    // super-user, the MANAGER desk (`view_management_dashboards`) or the Office
    // Admin desk (`list_properties`). A plain agent and the other desks stay out.
    canCorrectFarmListingLinks:
      role === "admin" ||
      isSuperuser ||
      has("view_management_dashboards") ||
      has("list_properties"),
    // EPIC #2033 WP11 (#2090): HR agent-compliance management capability.
    canManageCompliance: has("manage_compliance"),
    // #2183: may capture a deal ON BEHALF of another agent (Manager / Paralegal /
    // admin) -- selecting the lead agent for an absent agent.
    canCaptureOnBehalf: has("capture_on_behalf"),
    // A4 (#2317) / #3473: may allocate an agent to a farm area -- the one thing
    // that decides who can see an area's farm book. Admin / MANAGER / HR.
    canAssignAreas: has("assign_areas"),
    // #2813: the restricted HR/personnel document-template capability -- a
    // per-user grant (Natalie/Cam, optionally the paralegal), never a preset.
    canManageHRDocuments: has("manage_hr_documents"),
    // D9-INT-006 / D9-INT-012: may read a deal buyer's bond pack (the deal's
    // "Bond pack" tab). Off for the value-free presets (Manager / Accounts /
    // Office Admin / HR / Canvasser), exactly as they never see commission.
    canViewBondPack: has("view_bond_pack"),
    // D2-FRM-018 (#3714): office canvasser -- the whole farm book, read +
    // canvass (notes, owner contacts, tasks), no area ownership. Per-user grant.
    farmWideCanvass: Boolean(farmWideCanvass),
    // D2-USR-010 (#4001): works their own book (own farm areas) -- keeps the
    // own-book nav for a back-office preset holder. Nav-only. D4-FBK-4405:
    // never for the finance desk (finance / finance_accounts), whatever farm
    // areas it holds.
    hasOwnBook: Boolean(hasOwnBook) && !worksFinanceDesk(role, effectivePreset),
    has,
  };
}
